//modelisation de la page d'accueil
import React, { useState, useCallback, useEffect, memo, useMemo, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import Registration from './Registration';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Grid,
  Modal,
  Backdrop,
  Fade,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InstagramIcon from '@mui/icons-material/Instagram';

// Interfaces TypeScript pour les activités dynamiques
interface ActivityItem {
  id: number;
  title: string;
  description?: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
}

interface ActivitySection {
  id: number;
  name: string;
  title: string;
  description?: string;
  section_type: 'vie_academique' | 'moments_forts';
  order_index: number;
  items: ActivityItem[];
}

// Composant de navigation avec onglets
const NavigationBar = memo(() => {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    { 
      label: 'SCOLARITE', 
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: ' CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <AppBar 
      position="absolute" 
      sx={{ 
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px' // Ajuster pour éviter le chevauchement avec la bannière de contact
      }}
    >
      <Toolbar sx={{ 
        justifyContent: { xs: 'flex-end', md: 'space-between' },
        px: { xs: 2, md: 4 },
        py: { xs: 0.5, md: 1 },
        minHeight: { xs: 56, md: 64 }
      }}>
        {/* Logo - Masqué sur mobile */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 60,
            height: 60,
            border: '2px solid #87CEEB',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'white',
            overflow: 'hidden',
            p: 0.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <img
              src="/img/pages/logologo.webp"
              alt="La Maison des Enfants La petite Académie"
              loading="eager"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Navigation Desktop */}
        {!isMobile && (
          <Box sx={{ 
            display: 'flex', 
            gap: { md: 2, lg: 3 },
            alignItems: 'center'
          }}>
            {navigationItems.map((item) => (
              <Box key={item.label}>
                {item.hasSubmenu ? (
                  <>
                    <Button
                      onClick={handleScolariteMenuOpen}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderRadius: 0,
                        borderBottom: '2px solid transparent',
                        bgcolor: 'transparent',
                        '&:hover': {
                          bgcolor: 'transparent',
                          borderBottom: '2px solid #FF9800',
                          color: '#FF9800',
                        },
                        '&.active': {
                          borderBottom: '2px solid #FF9800',
                          color: '#FF9800',
                        }
                      }}
                    >
                      {item.label}
                      <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                    </Button>
                    <Menu
                      anchorEl={scolariteMenuAnchor}
                      open={Boolean(scolariteMenuAnchor)}
                      onClose={handleScolariteMenuClose}
                      sx={{
                        '& .MuiPaper-root': {
                          bgcolor: 'rgba(23, 128, 194, 0.95)',
                          color: 'white',
                          mt: 1,
                          backdropFilter: 'blur(10px)',
                          minWidth: 200
                        }
                      }}
                    >
                      {item.submenu?.map((subItem) => (
                        <MenuItem
                          key={subItem.label}
                          component={RouterLink}
                          to={subItem.path}
                          onClick={handleScolariteMenuClose}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {subItem.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                ) : item.isScroll ? (
                  <Button
                    onClick={handleContactsClick}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: { md: 12, lg: 14 },
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      px: { md: 1.5, lg: 2 },
                      py: 1,
                      borderRadius: 0,
                      borderBottom: '2px solid transparent',
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: 'transparent',
                        borderBottom: '2px solid #FF9800',
                        color: '#FF9800',
                      },
                      '&.active': {
                        borderBottom: '2px solid #FF9800',
                        color: '#FF9800',
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ) : (
                  <Button
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: { md: 12, lg: 14 },
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      px: { md: 1.5, lg: 2 },
                      py: 1,
                      borderRadius: 0,
                      borderBottom: '2px solid transparent',
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: 'transparent',
                        borderBottom: '2px solid #FF9800',
                        color: '#FF9800',
                      },
                      '&.active': {
                        borderBottom: '2px solid #FF9800',
                        color: '#FF9800',
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Menu Mobile - Masqué car géré dans ContactBanner */}
        {false && isMobile && (
          <>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              sx={{ color: 'white' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
              sx={{
                '& .MuiPaper-root': {
                  bgcolor: 'rgba(23, 128, 194, 0.95)',
                  color: 'white',
                  mt: 1,
                  backdropFilter: 'blur(10px)'
                }
              }}
            >
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <MenuItem
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                        <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                      </MenuItem>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={() => {
                              handleScolariteMenuClose();
                              handleMobileMenuClose();
                            }}
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <MenuItem
                      onClick={() => {
                        handleContactsClick();
                        handleMobileMenuClose();
                      }}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 14,
                        textTransform: 'uppercase',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      component={RouterLink}
                      to={item.path}
                      onClick={() => {
                        if (handleMobileMenuClose) handleMobileMenuClose();
                      }}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 14,
                        textTransform: 'uppercase',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  )}
                </Box>
              ))}
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
});

// Bannière de contact (réutilisable sur d'autres pages)
export const ContactBanner = memo(({ 
  showMobileMenu = false, 
  mobileMenuAnchor = null, 
  handleMobileMenuOpen = null, 
  handleMobileMenuClose = null, 
  navigationItems = [] 
}: {
  showMobileMenu?: boolean;
  mobileMenuAnchor?: HTMLElement | null;
  handleMobileMenuOpen?: ((event: React.MouseEvent<HTMLElement>) => void) | null;
  handleMobileMenuClose?: (() => void) | null;
  navigationItems?: Array<{
    label: string;
    path: string;
    hasSubmenu?: boolean;
    submenu?: Array<{ label: string; path: string; }>;
    isScroll?: boolean;
  }>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ 
      bgcolor: '#1780c2', 
      color: 'white', 
      py: 1,
      fontSize: { xs: 12, md: 14 },
      position: 'relative'
    }}>
      <Container maxWidth="lg">
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: { xs: 'space-between', md: 'space-between' },
          gap: { xs: 1, md: 2 },
          width: '100%',
          position: 'relative',
          pr: { xs: 8, md: 0 } // Espace réduit pour le menu mobile repositionné
        }}>
        {/* Contenu principal - Localisation et contact */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 1, md: 2 },
          flex: { xs: '1 1 auto', md: '1 1 auto' }
        }}>
          {/* Localisation */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap'
          }}>
            <LocationOnIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 'inherit' }}>
              Siège social: Abidjan, Cocody Riviera 3 - Crèche & Garderie
            </Typography>
          </Box>

          {/* Réseaux sociaux et contact */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}>
            {/* Nous suivre */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography sx={{ fontSize: 'inherit' }}>
                Nous suivre
              </Typography>
              <IconButton size="small" sx={{ color: 'white', p: 0.5 }}>
                <FacebookIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'white', p: 0.5 }}
                component="a"
                href="https://lapetiteacademie.ci:2096/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <EmailIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Boutons téléphone */}
            <Box sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap'
            }}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: '#FF9800',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  fontSize: 'inherit',
                  fontWeight: 600,
                  minWidth: 'auto',
                  '&:hover': {
                    bgcolor: '#F57C00',
                  },
                }}
                startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
              >
                (+225) 27 22 51 69 04
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: '#FF9800',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  fontSize: 'inherit',
                  fontWeight: 600,
                  minWidth: 'auto',
                  '&:hover': {
                    bgcolor: '#F57C00',
                  },
                }}
                startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
              >
                (+225) 07 08 02 24 24
              </Button>
            </Box>
          </Box>
        </Box>

      </Box>


    {/* Menu déroulant - en dehors du container principal */}
    {showMobileMenu && isMobile && (
      <>
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose || (() => {})}
          sx={{
            '& .MuiPaper-root': {
              bgcolor: 'rgba(23, 128, 194, 0.95)',
              color: 'white',
              mt: 1,
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          {navigationItems.map((item) => (
            <Box key={item.label}>
              {item.hasSubmenu ? (
                <>
                  <MenuItem
                    onClick={() => {
                      // Gérer le sous-menu ici si nécessaire
                      if (handleMobileMenuClose) handleMobileMenuClose();
                    }}
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: 'uppercase',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    {item.label}
                    <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                  </MenuItem>
                  {/* Sous-menu pour SCOLARITE */}
                  {item.submenu?.map((subItem) => (
                    <MenuItem
                      key={subItem.label}
                      component={RouterLink}
                      to={subItem.path}
                      onClick={() => {
                        if (handleMobileMenuClose) handleMobileMenuClose();
                      }}
                      sx={{
                        color: 'white',
                        fontWeight: 500,
                        fontSize: 13,
                        textTransform: 'uppercase',
                        pl: 4,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      {subItem.label}
                    </MenuItem>
                  ))}
                </>
              ) : item.isScroll ? (
                <MenuItem
                  onClick={() => {
                    const footerElement = document.getElementById('footer');
                    if (footerElement) {
                      footerElement.scrollIntoView({ behavior: 'smooth' });
                    }
                    if (handleMobileMenuClose) handleMobileMenuClose();
                  }}
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 14,
                    textTransform: 'uppercase',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {item.label}
                </MenuItem>
              ) : (
                <MenuItem
                  component={RouterLink}
                  to={item.path}
                  onClick={() => {
                    if (handleMobileMenuClose) handleMobileMenuClose();
                  }}
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 14,
                    textTransform: 'uppercase',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {item.label}
                </MenuItem>
              )}
            </Box>
          ))}
        </Menu>
      </>
    )}
    </Container>
    
  </Box>
  );
});

// Footer (réutilisable sur d'autres pages)
export const SiteFooter = memo(() => (
  <Box id="footer" sx={{ 
    bgcolor: '#1780c2', 
    py: 6
  }}>
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        {/* Informations de contact et carte - Colonne droite */}
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%' }}>
            {/* Section Contact */}
            <Box sx={{ 
              bgcolor: 'white',
              borderRadius: 3,
              p: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              mb: 4
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1, 
                  color: '#1780c2',
                  fontSize: { xs: 24, md: 28 }
                }}
              >
                Contact
              </Typography>
              
              {/* Ligne de soulignement */}
              <Box sx={{
                width: 60,
                height: 3,
                bgcolor: '#1780c2',
                mb: 4
              }} />
              
              {/* Informations de contact */}
              <Box sx={{ space: 3 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  gap: 2
                }}>
                  <EmailIcon sx={{ color: '#1780c2', fontSize: 24 }} />
                  <Typography sx={{ 
                    color: '#333',
                    fontSize: { xs: 14, md: 16 }
                  }}>
                    infos@lapetiteacademie.ci
                  </Typography>
                </Box>
                
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  gap: 2
                }}>
                  <PhoneIcon sx={{ color: '#1780c2', fontSize: 24 }} />
                  <Typography sx={{ 
                    color: '#333',
                    fontSize: { xs: 14, md: 16 }
                  }}>
                    (+225) 27 22 51 69 04
                  </Typography>
                </Box>
                
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <PhoneIcon sx={{ color: '#1780c2', fontSize: 24 }} />
                  <Typography sx={{ 
                    color: '#333',
                    fontSize: { xs: 14, md: 16 }
                  }}>
                    (+225) 07 08 02 24 24
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Carte Google Maps */}
            <Box sx={{ 
              bgcolor: 'white',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              height: 400
            }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7944.808854936596!2d-3.9521382!3d5.3550939!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfc1ed731c611de7%3A0xe55521e75b8421cb!2sLA%20MAISON%20DES%20ENFANTS%20-%20LA%20PETITE%20ACADEMIE!5e0!3m2!1sfr!2sci!4v1657296019947!5m2!1sfr!2sci"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="La Petite Académie - Abidjan"
              />
            </Box>
          </Box>
        </Grid>

        {/* Colonne gauche - Informations de l'école */}
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%' }}>
            {/* Section Informations */}
            <Box sx={{ 
              bgcolor: 'white',
              borderRadius: 3,
              p: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              mb: 4
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1, 
                  color: '#1780c2',
                  fontSize: { xs: 24, md: 28 }
                }}
              >
                La Petite Académie
              </Typography>
              
              {/* Ligne de soulignement */}
              <Box sx={{
                width: 60,
                height: 3,
                bgcolor: '#1780c2',
                mb: 4
              }} />
              
              <Typography sx={{ 
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.6,
                mb: 3
              }}>
                La Maison des Enfants - La Petite Académie est un établissement d'éducation préscolaire 
                dédié à l'épanouissement et au développement harmonieux de chaque enfant.
              </Typography>
              
              <Typography sx={{ 
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.6,
                mb: 3
              }}>
                Nous offrons un environnement bienveillant et sécurisé où les enfants peuvent 
                apprendre, grandir et s'épanouir à leur rythme.
              </Typography>

              {/* Réseaux sociaux */}
              <Box sx={{ mt: 4 }}>
                <Typography sx={{ 
                  color: '#1780c2',
                  fontWeight: 600,
                  mb: 2,
                  fontSize: { xs: 14, md: 16 }
                }}>
                  Suivez-nous
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <IconButton sx={{ color: '#1877F2' }}>
                    <FacebookIcon />
                  </IconButton>
                  <IconButton sx={{ color: '#E4405F' }}>
                    <InstagramIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Copyright */}
            <Box sx={{ 
              bgcolor: '#333',
              color: 'white',
              borderRadius: 3,
              p: 3,
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Copyrights ® 2025 La Maison des Enfants La petite Académie. All rights reserved
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
));

// Carousel traditionnel personnalisé
const TraditionalCarousel = memo(() => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sliderImages = useMemo(() => [
    { 
      src: '/img/pages/ac.jpg', 
      alt: 'Pré-inscription',
      title: 'Bienvenue',
      subtitle: 'à',
      description: 'la Maison des Enfants - La Petite Académie',
      type: 'scolarite'
    },
    { 
      src: '/img/pages/ac2.jpg', 
      alt: 'Crèche - Garderie',
      title: '',
      subtitle: '',
      description: '',
      diferent: 'Un cadre d\'accueil propice au développement intellectuel, physique et émotionnel des enfants',
      type: 'creche'
    },
    { 
      src: '/img/pages/ac3.jpg', 
      alt: 'Crèche - Garderie',
      title: '',
      subtitle: '',
      description: '',
      diferent: 'Nous proposons un programme bilingue orienté sur une approche alternative',
      type: 'creche'
    },
  ], []);

  // Préchargement simple des images
  useEffect(() => {
    sliderImages.forEach((image) => {
      const img = new Image();
      img.src = image.src;
    });
  }, [sliderImages]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3); // 3 images fixes
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Reprendre l'auto-play après 5 secondes (réduit de 10s à 5s)
    setTimeout(() => setIsAutoPlaying(true), 5000);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + 3) % 3); // 3 images fixes
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % 3); // 3 images fixes
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 5000);
  }, []);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh',
      position: 'relative', 
      bgcolor: '#1780c2',
      overflow: 'hidden'
    }}>
      {/* Container principal du carousel */}
      <Box sx={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Images du carousel */}
        {sliderImages.map((image, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: currentSlide === index ? 1 : 0,
              transition: 'opacity 0.8s ease-in-out',
              zIndex: currentSlide === index ? 2 : 1,
              visibility: 'visible',
            }}
          >
            {/* Background blur pour l'effet */}
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${image.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(10px)',
              transform: 'scale(1.08)',
              zIndex: 0,
              willChange: 'transform'
            }} />
            
            {/* Image principale */}
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <img
                src={image.src}
                alt={image.alt}
                loading="eager"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  display: 'block',
                  backgroundColor: '#f0f0f0'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.backgroundColor = '#1780c2';
                  target.style.display = 'flex';
                  target.style.alignItems = 'center';
                  target.style.justifyContent = 'center';
                  target.style.color = 'white';
                  target.style.fontSize = '18px';
                }}
              />
            </Box>
          </Box>
        ))}

        {/* Boutons de navigation */}
        <IconButton
          onClick={goToPrevious}
          sx={{
            position: 'absolute',
            left: { xs: 20, md: 40 },
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            color: '#1780c2',
            zIndex: 3,
            '&:hover': {
              bgcolor: 'white',
            },
            width: { xs: 50, md: 60 },
            height: { xs: 50, md: 60 },
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
        </IconButton>

        <IconButton
          onClick={goToNext}
          sx={{
            position: 'absolute',
            right: { xs: 20, md: 40 },
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            color: '#1780c2',
            zIndex: 3,
            '&:hover': {
              bgcolor: 'white',
            },
            width: { xs: 50, md: 60 },
            height: { xs: 50, md: 60 },
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <ArrowForwardIosIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
        </IconButton>

        {/* Indicateurs de pagination */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: 30, md: 40 },
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
          zIndex: 3
        }}>
          {sliderImages.map((_, index) => (
            <IconButton
              key={index}
              onClick={() => goToSlide(index)}
              sx={{
                p: 0.5,
                color: currentSlide === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  color: 'white',
                }
              }}
            >
              {currentSlide === index ? (
                <RadioButtonCheckedIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
              )}
            </IconButton>
          ))}
        </Box>

        {/* Overlay avec texte structuré */}
        <Box sx={{
          position: 'absolute',
          top: { xs: '20%', md: '40%' },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          textAlign: 'center',
          width: { xs: '90%', md: '80%' }
        }}>
          {/* Titre principal - "Bienvenue" */}
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: 32, md: 48, lg: 64 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: 2
            }}
          >
            {sliderImages[currentSlide].title}
          </Typography>
          
          {/* Sous-titre - "à" */}
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 400,
              fontSize: { xs: 18, md: 24, lg: 45 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              mb: 1
            }}
          >
            {sliderImages[currentSlide].subtitle}
          </Typography>
          
          {/* Description - "la Maison des Enfants - La Petite Académie" */}
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 400,
              fontSize: { xs: 16, md: 20, lg: 45 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              textAlign: 'center'
            }}
          >
            {sliderImages[currentSlide].description}
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: 16, md: 45, lg: 45 },
              textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
              textAlign: 'center'
            }}
          >
            {sliderImages[currentSlide].diferent}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

// Section d'informations avec les trois cartes
const InfoSection = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const cards = useMemo(() => [
    {
      title: "CRÈCHE/GARDERIE TPS",
      paragraphs: [
        "Bienvenue dans notre crèche/garderie et maternelle, un lieu où chaque enfant s'épanouit dans la bienveillance et la joie d'apprendre.",
        "Nous offrons un cadre sécurisé, chaleureux et adapté à chaque âge, où les tout-petits découvrent, explorent et grandissent à leur rythme.",
        "À travers des activités ludiques et éducatives, nous accompagnons pas à pas leur éveil, leur curiosité et leur autonomie, en tissant chaque jour un lien de confiance avec les familles"
      ]
    },
    {
      title: "MATERNELLE",
      paragraphs: [
        "La maternelle est le premier pas vers le monde scolaire. À La Maison des Enfants - La petite académie, nous mettons l'accent sur l'éveil intellectuel, la créativité et la socialisation.",
        "Grâce à des méthodes pédagogiques adaptées, vos enfants acquièrent les bases de la lecture, de l'écriture, du langage et des mathématiques, tout en développant leur curiosité et leur autonomie."
      ]
    },
    {
      title: "APPROCHE PEDAGOGIQUE",
      paragraphs: [
        "Notre pédagogie s'inspire de la méthode Montessori, qui place l'enfant au centre de son apprentissage. Chaque activité est conçue pour encourager son autonomie, sa confiance et son sens des responsabilités.",
        "Dans un environnement préparé et sécurisé, l'enfant choisit, expérimente et progresse à son rythme. Nos éducateurs l'accompagnent avec bienveillance, afin de développer sa concentration, sa curiosité et son plaisir d'apprendre."
      ]
    }
  ], []);

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      minHeight: { xs: '80vh', md: '100vh' },
      backgroundImage: 'url(/img/pages/38.jpg)', // Image de fond d'école
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'local',
      backgroundRepeat: 'no-repeat',
      // Fallback background si l'image ne se charge pas
      backgroundColor: '#f0f8ff', // Bleu très clair comme fallback
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 4, md: 6 }
    }}>
      {/* Overlay sombre pour la lisibilité */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.3)',
        zIndex: 1
      }} />

      {/* Container des cartes */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
          maxWidth: '1200px',
          mx: 'auto'
        }}>
          {cards.map((card, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: '#1a4d73', // Bleu plus foncé comme sur l'image
                borderRadius: '0 0 30px 30px', // Haut carré, bas arrondi
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: { xs: 400, md: 450 }
              }}
            >
              {/* Header orange */}
              <Box sx={{
                bgcolor: '#FF9800',
                py: { xs: 1.5, md: 2 },
                px: { xs: 2, md: 2.5 },
                textAlign: 'center',
                border: '4px solid white'
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: 16, md: 18 },
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    lineHeight: 1.2,
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {card.title}
                </Typography>
              </Box>

              {/* Contenu */}
              <Box sx={{
                flex: 1,
                p: { xs: 2, md: 2.5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                pt: { xs: 2.5, md: 3 }
              }}>
                {card.paragraphs.map((paragraph, paragraphIndex) => (
                  <Typography
                    key={paragraphIndex}
                    variant="body1"
                    sx={{
                      color: 'white',
                      fontSize: { xs: 15, md: 16 },
                      lineHeight: 1.6,
                      textAlign: 'left',
                      mb: paragraphIndex < card.paragraphs.length - 1 ? 2 : 0,
                      fontFamily: 'Arial, sans-serif',
                      fontWeight: 400
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
});

// Section Mot de la Directrice
const DirectorSection = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{
      width: '100%',
      bgcolor: 'white',
      position: 'relative'
    }}>
      {/* Header jaune avec motifs */}
      <Box sx={{
        bgcolor: '#FFD700', // Jaune vif
        py: 2,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Motifs décoratifs */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/img/pages/colors.png)', // Utiliser une image de motifs
          backgroundSize: 'contain',
          backgroundRepeat: 'repeat-x',
          backgroundPosition: 'center',
          opacity: 0.3
        }} />
      </Box>

      {/* Contenu principal */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center'
        }}>
          {/* Texte à gauche */}
          <Box sx={{
            position: 'relative',
            bgcolor: 'white',
            p: { xs: 3, md: 4 },
            borderRadius: 2,
          
          }}>
            <Typography
              variant="h3"
              sx={{
                color: '#1780c2',
                fontWeight: 700,
                fontSize: { xs: 24, md: 32 },
                mb: 3,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              MOT DE LA DIRECTRICE
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.7,
                mb: 2,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              C'est avec une immense joie et une passion profonde pour l'éducation de la petite enfance que je vous accueille dans notre établissement. Mon parcours, enrichi par des expériences au Canada et en Côte d'Ivoire, m'a permis de développer une approche pédagogique unique qui place l'enfant au cœur de son apprentissage.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.7,
                mb: 2,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              À La Maison des Enfants – La Petite Académie, nous croyons fermement que chaque enfant possède un potentiel extraordinaire. Notre mission est de créer un environnement bienveillant, stimulant et sécurisé où chaque petit peut s'épanouir, découvrir et grandir à son rythme.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#333',
                fontSize: { xs: 14, md: 16 },
                lineHeight: 1.7,
                mb: 3,
                fontFamily: 'Arial, sans-serif'
              }}
            >
              Nous nous engageons à accompagner vos enfants dans leur développement global, en cultivant leur curiosité, leur créativité et leur confiance en eux. Ensemble, construisons l'avenir de nos petits champions !
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#1780c2',
                fontWeight: 600,
                fontSize: { xs: 16, md: 18 },
                textAlign: 'right',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              Fondatrice/Directrice<br />
              Mme. Binaté Bakayoko
            </Typography>
          </Box>

          {/* Image à droite */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Box sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              border: '8px solid rgb(50, 91, 205)', // Vert lime
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              maxWidth: { xs: '100%', md: '400px' },
              width: '100%'
            }}>
              <img
                src="/img/pages/dir.jpeg"
                alt="Mme. Binaté Bakayoko, Directrice"
                loading="lazy"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer bleu avec motifs */}
      <Box sx={{
        bgcolor: '#1780c2',
        py: 2,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Motifs décoratifs */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/img/pages/colors.png)',
          backgroundSize: 'contain',
          backgroundRepeat: 'repeat-x',
          backgroundPosition: 'center',
          opacity: 0.2
        }} />
      </Box>
    </Box>
  );
});

// Section Programme Bilingue
const BilingualProgramSection = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const activities = useMemo(() => [
    "Approche alternative",
    "Activités périscolaires",
    "Initiation à la robotique dès 4 ans",
    "Initiation à la philosophie dès 3 ans",
    "Initiation à l'anglais dès 2 ans",
    "Sorties éducatives et découvertes"
  ], []);

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      minHeight: { xs: '80vh', md: '100vh' },
      backgroundImage: 'url(/img/pages/38.jpg)', // Image de fond avec jouets
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'local',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 4, md: 6 }
    }}>
      {/* Header bleu avec icônes */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '80px',
        bgcolor: '#1780c2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {/* Icônes décoratives */}
        <Box sx={{
          display: 'flex',
          gap: 3,
          alignItems: 'center',
          opacity: 0.8
        }}>
          <Box sx={{ color: 'white', fontSize: 24 }}>🏠</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>🌳</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>⭐</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>☁️</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>👤</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>🎨</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>📚</Box>
          <Box sx={{ color: 'white', fontSize: 24 }}>🎯</Box>
        </Box>
      </Box>

      {/* Overlay bleu pour la lisibilité */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(23, 128, 194, 0.7)',
        zIndex: 1
      }} />

      {/* Contenu principal */}
      <Container maxWidth="lg" sx={{ 
        position: 'relative', 
        zIndex: 2,
        mt: 10, // Pour éviter le header
        mb: 4
      }}>
        {/* Titre */}
        <Typography
          variant="h2"
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: { xs: 28, md: 40, lg: 48 },
            textAlign: 'center',
            mb: 4,
            fontFamily: 'Arial, sans-serif',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
          }}
        >
          PROGRAMME BILINGUE
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: 'white',
            fontSize: { xs: 16, md: 18 },
            lineHeight: 1.7,
            textAlign: 'left',
            mb: 6,
            fontFamily: 'Arial, sans-serif',
            maxWidth: '800px',
            mx: 'auto',
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
          }}
        >
          Nous proposons un programme bilingue français-anglais, pensé pour éveiller naturellement les enfants à la richesse de deux cultures. À travers des jeux, des chansons, des histoires et des activités quotidiennes, ils découvrent et pratiquent une seconde langue dans un cadre ludique et immersif, favorisant leur ouverture au monde et leur confiance en soi.
        </Typography>

        {/* Grille des activités */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: { xs: 2, md: 3 },
          maxWidth: '800px',
          mx: 'auto',
          gridTemplateRows: 'repeat(3, 1fr)'
        }}>
          {activities.map((activity, index) => (
            <Button
              key={index}
              variant="contained"
              sx={{
                bgcolor: '#32CD32', // Vert lime
                color: 'white',
                py: { xs: 2, md: 2.5 },
                px: 3,
                fontSize: { xs: 14, md: 16 },
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                fontFamily: 'Arial, sans-serif',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  bgcolor: '#28B828',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              {activity}
            </Button>
          ))}
        </Box>
      </Container>

      {/* Indicateur de scroll */}
      <Box sx={{
        position: 'absolute',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2
      }}>
        <Box sx={{
          color: 'white',
          fontSize: 24,
          animation: 'bounce 2s infinite',
          '@keyframes bounce': {
            '0%, 20%, 50%, 80%, 100%': {
              transform: 'translateY(0)',
            },
            '40%': {
              transform: 'translateY(-10px)',
            },
            '60%': {
              transform: 'translateY(-5px)',
            },
          }
        }}>
          ▼
        </Box>
      </Box>
    </Box>
  );
});

// Section Galerie d'activités dynamique
const ActivitiesGallerySection = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Fonction pour obtenir l'URL correcte de l'image
  const getImageUrl = useCallback((imageUrl: string) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    return `https://lapetiteacademie.ci${imageUrl}`;
  }, []);

  // Chargement des activités depuis l'API
  useEffect(() => {
    const loadActivities = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://lapetiteacademie.ci/api/dynamic-pages/home-activities');
        
        if (response.data.status === 'success' && response.data.data) {
          // Filtrer seulement les activités visibles et les trier par ordre d'affichage
          const visibleActivities = response.data.data
            .filter((config: any) => config.is_visible)
            .sort((a: any, b: any) => a.display_order - b.display_order)
            .map((config: any) => ({
              id: config.activity_id,
              title: config.custom_title || config.original_title || 'Activité',
              description: config.custom_description || config.original_description,
              image_url: config.custom_image_url || config.original_image_url || '/img/pages/default.jpg',
              alt_text: config.alt_text,
              order_index: config.display_order
            }));
          
          setActivities(visibleActivities);
        }
        } catch (error) {
          console.error('Erreur lors du chargement des activités:', error);
          // En cas d'erreur, ne pas afficher d'activités par défaut
          setActivities([]);
        } finally {
          setLoading(false);
        }
    };

    loadActivities();
  }, []);

  if (loading) {
    return (
      <Box sx={{
        width: '100%',
        bgcolor: 'white',
        position: 'relative',
        py: 4
      }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              color: '#1A4D73',
              fontWeight: 700,
              fontSize: { xs: 24, md: 32, lg: 36 },
              textAlign: 'center',
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Chargement des activités...
          </Typography>
        </Container>
      </Box>
    );
  }

  // Si aucune activité n'est disponible, ne pas afficher la section
  if (activities.length === 0) {
    return null;
  }

  return (
    <Box sx={{
      width: '100%',
      bgcolor: 'white',
      position: 'relative'
    }}>
      {/* Header vert clair avec motifs */}
      <Box sx={{
        bgcolor: '#ADFF2F', // Vert lime plus vibrant
        py: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Motifs décoratifs organiques */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/img/pages/colors.png)',
          backgroundSize: '180px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center',
          opacity: 0.2,
          filter: 'blur(0.3px)'
        }} />

        {/* Titre principal */}
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: 24, md: 32, lg: 36 },
              textAlign: 'center',
              fontFamily: 'Arial, sans-serif',
              textShadow: '2px 2px 4px rgba(0,0,0,0.4)',
              position: 'relative',
              zIndex: 2,
              lineHeight: 1.2
            }}
          >
            Découvrez nos dernières activités en images
          </Typography>
        </Container>
      </Box>

      {/* Section des images avec titres */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 0, // Pas d'espacement entre les images
          alignItems: 'start'
        }}>
          {activities.map((activity, index) => (
            <Box
              key={activity.id || index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}
            >
              {/* Image de l'activité */}
              <Box sx={{
                width: '100%',
                height: { xs: 250, md: 350 },
                overflow: 'hidden',
                border: '4px solid white', // Bordure blanche épaisse comme sur l'image
                mb: 2
              }}>
                <img
                  src={getImageUrl(activity.image_url)}
                  alt={activity.alt_text || activity.title}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://picsum.photos/400/350?random=${activity.id}`;
                  }}
                />
              </Box>

              {/* Titre de l'activité */}
              <Typography
                variant="h6"
                sx={{
                  color: '#1A4D73', // Bleu foncé comme sur l'image
                  fontWeight: 'bold',
                  fontSize: { xs: 16, md: 27 },
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.3,
                  textAlign: 'center',
                  mt: 1
                }}
              >
                {activity.title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Bande verte incurvée en bas */}
      <Box sx={{
        width: '100%',
        height: 60,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Bande verte avec forme incurvée */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100%',
          bgcolor: '#32CD32', // Vert lime vif
          clipPath: 'polygon(0 40%, 100% 0%, 100% 100%, 0% 100%)' // Forme incurvée
        }} />
      </Box>
    </Box>
  );
});

// Section Call-to-Action Inscription
const RegistrationCTASection = memo(({ showAdminButton }: { showAdminButton: boolean }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openRegistrationModal, setOpenRegistrationModal] = useState(false);

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: { xs: '40vh', md: '50vh' }, // Section plus petite
      backgroundImage: 'url(/img/pages/38.jpg)', // Image de fond de classe
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'local',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Overlay bleu sombre pour la lisibilité */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(23, 128, 194, 0.6)', // Overlay bleu comme dans l'image
        zIndex: 1
      }} />

      {/* Boutons centrés */}
      <Box sx={{
        position: 'relative',
        zIndex: 2,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'center'
      }}>
        {/* Bouton d'inscription principal */}
        <Button
          variant="contained"
          onClick={() => setOpenRegistrationModal(true)}
          sx={{
            bgcolor: '#F7931E', // Orange exact de l'image
            color: 'white',
            fontSize: { xs: 18, md: 22, lg: 26 },
            fontWeight: 700,
            py: { xs: 2, md: 2.5 },
            px: { xs: 4, md: 5 },
            borderRadius: 3, // Coins plus arrondis comme sur l'image
            textTransform: 'none',
            fontFamily: 'Arial, sans-serif',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            minWidth: { xs: 220, md: 280 },
            border: '2px solid white', // Bordure blanche comme sur l'image
            '&:hover': {
              bgcolor: '#E8893A',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            },
            transition: 'all 0.3s ease'
          }}
        >
          J'inscris mon enfant
        </Button>

        {/* Bouton d'administration (caché par défaut) */}
        {showAdminButton && (
          <Button
            variant="contained"
            component={RouterLink}
            to="/secretary-login"
            sx={{
              bgcolor: '#dc3545', // Rouge pour l'administration
              color: 'white',
              fontSize: { xs: 14, md: 16 },
              fontWeight: 600,
              py: { xs: 1.5, md: 2 },
              px: { xs: 3, md: 4 },
              borderRadius: 2,
              textTransform: 'none',
              fontFamily: 'Arial, sans-serif',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              minWidth: { xs: 180, md: 200 },
              border: '2px solid white',
              animation: 'fadeIn 0.5s ease-in',
              '&:hover': {
                bgcolor: '#c82333',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Connexion Admin
          </Button>
        )}
      </Box>

      {/* Animation CSS pour le bouton admin */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Modal de pré-inscription */}
      <Modal
        open={openRegistrationModal}
        onClose={() => setOpenRegistrationModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Fade in={openRegistrationModal}>
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
              maxHeight: '90vh',
              overflow: 'auto',
              outline: 'none',
            }}
          >
            <Suspense fallback={<div>Chargement...</div>}>
              <Registration 
                onClose={() => setOpenRegistrationModal(false)} 
                isMobile={isMobile}
              />
            </Suspense>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
});

// Composant principal Home
const Home = memo(() => {
  const [showAdminButton, setShowAdminButton] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Debug pour vérifier la détection mobile
  useEffect(() => {
    console.log('isMobile:', isMobile);
  }, [isMobile]);

  const handleMobileMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  }, []);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuAnchor(null);
  }, []);

  const navigationItems = useMemo(() => [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    { 
      label: 'SCOLARITE', 
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ], []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isWindows = event.ctrlKey && event.altKey && event.key.toLowerCase() === 'a';
      const isMac = event.metaKey && event.altKey && event.key.toLowerCase() === 'a';
      
      if (isWindows || isMac) {
        event.preventDefault();
        setShowAdminButton(true);
        // Masquer automatiquement après 3 secondes
        setTimeout(() => setShowAdminButton(false), 3000);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Crèche Garderie Maternelle Bilingue Abidjan - La Petite Académie | Méthode Montessori</title>
        <meta name="description" content="La Maison des Enfants - La Petite Académie : Crèche, garderie et maternelle bilingue à Abidjan Cocody Riviera 3. Méthode Montessori, programme bilingue français-anglais, éveil et développement harmonieux des enfants. Inscription 2025-2026." />
        <meta name="keywords" content="crèche Abidjan, garderie Abidjan, école maternelle Abidjan, La Petite Académie, méthode Montessori, programme bilingue, français-anglais, éveil intellectuel, développement harmonieux, pédagogie alternative, autonomie, bienveillance, activités ludiques, jeux éducatifs, initiation robotique, sorties éducatives, Cocody Riviera 3" />
        <meta property="og:title" content="Crèche Garderie Maternelle Bilingue Abidjan - La Petite Académie | Méthode Montessori" />
        <meta property="og:description" content="La Maison des Enfants - La Petite Académie : Crèche, garderie et maternelle bilingue à Abidjan Cocody Riviera 3. Méthode Montessori, programme bilingue français-anglais, éveil et développement harmonieux des enfants." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lapetiteacademie.ci" />
        <link rel="canonical" href="https://lapetiteacademie.ci" />
      </Helmet>
      <Box sx={{ 
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner 
          showMobileMenu={false}
          mobileMenuAnchor={mobileMenuAnchor}
          handleMobileMenuOpen={handleMobileMenuOpen}
          handleMobileMenuClose={handleMobileMenuClose}
          navigationItems={navigationItems}
        />
        
        {/* Menu Mobile - Bouton flottant distinctif repositionné */}
        <Box sx={{
          position: 'fixed',
          top: { xs: 70, sm: 80 }, // Positionné sous la bannière de contact
          right: 15,
          zIndex: 99999,
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 0.5
        }}>
          {/* Label pour clarifier la fonction */}
          <Typography sx={{
            color: '#FF6B35',
            fontSize: 10,
            fontWeight: 'bold',
            textAlign: 'center',
            textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
            backgroundColor: 'rgba(255,255,255,0.9)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            border: '1px solid #FF6B35'
          }}>
            MENU
          </Typography>
          
          <Button
            variant="contained"
            onClick={handleMobileMenuOpen}
            sx={{ 
              color: 'white',
              p: 0,
              bgcolor: '#FF6B35', // Orange vif pour se distinguer des boutons téléphone
              borderRadius: '50%',
              border: '3px solid white',
              boxShadow: '0 8px 25px rgba(255, 107, 53, 0.8)',
              width: 60,
              height: 60,
              minWidth: 60,
              minHeight: 60,
              '&:hover': {
                bgcolor: '#E55A2B',
                border: '3px solid #FFD700',
                transform: 'scale(1.1)',
                boxShadow: '0 12px 35px rgba(255, 107, 53, 1)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <MenuIcon sx={{ fontSize: 28, fontWeight: 'bold' }} />
          </Button>
        </Box>
        
        {/* Menu déroulant mobile */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiPaper-root': {
              bgcolor: 'rgba(23, 128, 194, 0.95)',
              color: 'white',
              mt: 1,
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          {navigationItems.map((item) => (
            <MenuItem
              key={item.label}
              component={item.isScroll ? 'div' : RouterLink}
              to={item.isScroll ? undefined : item.path}
              onClick={() => {
                if (item.isScroll) {
                  const footerElement = document.getElementById('footer');
                  if (footerElement) {
                    footerElement.scrollIntoView({ behavior: 'smooth' });
                  }
                }
                handleMobileMenuClose();
              }}
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: 14,
                textTransform: 'uppercase',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
        
        <NavigationBar />
        <TraditionalCarousel />
        <InfoSection />
        <DirectorSection />
        <BilingualProgramSection />
        <ActivitiesGallerySection />
        <RegistrationCTASection showAdminButton={showAdminButton} />
        <SiteFooter />
      </Box>
    </>
  );
});

export default Home;